// HTTP client for the Celeris chat-completions endpoint.
// The browser talks to Celeris directly (the endpoint sends open CORS headers)
// so SSE streams with no middleman. The shared demo key is intentionally
// client-side; the /api origin exists only for agent review publishing.

import { PRI, type Priority, scheduler, sleep } from "./scheduler";

export const API = {
  base: "https://ap-southeast-2.aws.cloud.celeris.ai/diffusiongemma-fp8/v1",
  key: "dg_0x2PZet3NFRlg-JlbSIEz4pw5CP0e73Fw03Q5QpVbiA",
  model: "diffusiongemma-fp8",
};

export interface ChatBody {
  model: string;
  max_tokens: number;
  temperature: number;
  stream?: boolean;
  messages: { role: string; content: string }[];
}

function rawFetch(body: ChatBody, signal?: AbortSignal): Promise<Response> {
  return fetch(`${API.base}/chat/completions`, {
    method: "POST", signal,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${API.key}` },
    body: JSON.stringify(body),
  });
}

export interface ApiFetchResult {
  res: Response;
  httpRetries: number;   // 429/503 rounds absorbed before this response
}

export async function apiFetch(
  body: ChatBody,
  { signal, priority = PRI.FG }: { signal?: AbortSignal; priority?: Priority } = {},
): Promise<ApiFetchResult> {
  // Prefetch is best-effort: give up quickly so it never drains the shared
  // budget away from foreground moves. Foreground/grounding are patient.
  const maxAttempts = priority <= PRI.PREFETCH ? 3 : 9;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const res = await scheduler.enqueue(() => rawFetch(body, signal), priority);
    if (res.status === 429 || res.status === 503) {
      scheduler.noteRateLimit();     // stretch the send gap until the bucket recovers
      const ra = parseFloat(res.headers.get("retry-after") ?? "");
      const wait = (Number.isFinite(ra) ? ra * 1000 : 400) + attempt * 180 + Math.random() * 200;
      await sleep(wait);
      continue;
    }
    if (!res.ok) throw new Error(`api ${res.status}`);
    return { res, httpRetries: attempt };
  }
  throw new Error("rate-limited");
}
